import {
  Badge,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { IconDatabase, IconSearch } from '@tabler/icons-react'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  listCatalogs,
  listSchemas,
  listTables,
  type UnityCatalogTable,
} from '../../api/databricks'
import { Empty, IntelligencePageHeader, QueryState } from '../intelligence/shared'

function formatUpdatedAt(epochMs?: number): string {
  if (!epochMs) return '—'
  return new Date(epochMs).toLocaleDateString()
}

function TableColumns({ table }: { table: UnityCatalogTable }) {
  if (!table.columns?.length) {
    return <Empty message="No column metadata is available for this table." />
  }
  return (
    <Table.ScrollContainer minWidth={480}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Column</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Comment</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {table.columns.map((column) => (
            <Table.Tr key={column.name}>
              <Table.Td>
                <Text size="sm" fw={600}>
                  {column.name}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge variant="light" color="gray" size="sm">
                  {column.type_text ?? column.type_name ?? 'unknown'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {column.comment ?? '—'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

export function DataCatalog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const catalogName = searchParams.get('catalog')?.trim() ?? ''
  const schemaName = searchParams.get('schema')?.trim() ?? ''
  const tableName = searchParams.get('table')?.trim() ?? ''
  const search = searchParams.get('q')?.trim() ?? ''

  const catalogsQuery = useQuery({
    queryKey: ['databricks', 'uc', 'catalogs'],
    queryFn: listCatalogs,
  })

  const schemasQuery = useQuery({
    queryKey: ['databricks', 'uc', 'schemas', catalogName],
    queryFn: () => listSchemas(catalogName),
    enabled: Boolean(catalogName),
  })

  const tablesQuery = useQuery({
    queryKey: ['databricks', 'uc', 'tables', catalogName, schemaName],
    queryFn: () => listTables(catalogName, schemaName),
    enabled: Boolean(catalogName) && Boolean(schemaName),
  })

  const catalogOptions = useMemo(
    () =>
      catalogsQuery.data?.map((catalog) => ({
        value: catalog.name,
        label: catalog.name,
      })) ?? [],
    [catalogsQuery.data]
  )

  const schemaOptions = useMemo(
    () =>
      schemasQuery.data?.map((schema) => ({
        value: schema.name,
        label: schema.name,
      })) ?? [],
    [schemasQuery.data]
  )

  const filteredTables = useMemo(() => {
    const term = search.toLowerCase()
    const tables = tablesQuery.data ?? []
    if (!term) return tables
    return tables.filter((table) =>
      [table.name, table.comment, table.owner, table.table_type].some(
        (field) => field?.toLowerCase().includes(term)
      )
    )
  }, [tablesQuery.data, search])

  const selectedTable = tablesQuery.data?.find((t) => t.name === tableName)

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next)
  }

  const selectCatalog = (value: string | null) =>
    updateParams({ catalog: value, schema: null, table: null })
  const selectSchema = (value: string | null) =>
    updateParams({ schema: value, table: null })
  const selectTable = (name: string | null) => updateParams({ table: name })

  return (
    <Stack>
      <IntelligencePageHeader
        title="Data catalog"
        description="Browse catalogs, schemas, and tables from the connected Databricks workspace's Unity Catalog metastore."
      />

      <Group grow align="flex-start">
        <Select
          searchable
          clearable
          value={catalogName || null}
          onChange={selectCatalog}
          data={catalogOptions}
          disabled={catalogsQuery.isLoading}
          placeholder="Select a catalog"
          label="Catalog"
          leftSection={<IconDatabase size={16} />}
        />
        <Select
          searchable
          clearable
          value={schemaName || null}
          onChange={selectSchema}
          data={schemaOptions}
          disabled={!catalogName || schemasQuery.isLoading}
          placeholder={catalogName ? 'Select a schema' : 'Select a catalog first'}
          label="Schema"
        />
      </Group>

      <QueryState error={catalogsQuery.error} loading={catalogsQuery.isLoading} />
      {!catalogName ? null : (
        <QueryState error={schemasQuery.error} loading={schemasQuery.isLoading} />
      )}

      {catalogName && schemaName && (
        <>
          <TextInput
            value={search}
            onChange={(event) => updateParams({ q: event.currentTarget.value || null })}
            placeholder="Search tables by name, owner, type, or comment"
            leftSection={<IconSearch size={16} />}
          />
          <QueryState error={tablesQuery.error} loading={tablesQuery.isLoading} />
          {!tablesQuery.isLoading &&
            !tablesQuery.error &&
            (filteredTables.length ? (
              <Table.ScrollContainer minWidth={720}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Table</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Format</Table.Th>
                      <Table.Th>Owner</Table.Th>
                      <Table.Th>Updated</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredTables.map((table) => (
                      <Table.Tr
                        key={table.full_name}
                        onClick={() => selectTable(table.name)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.Td>
                          <Text fw={600} size="sm">
                            {table.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {table.comment ?? table.full_name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {table.table_type ?? 'unknown'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{table.data_source_format ?? '—'}</Table.Td>
                        <Table.Td>{table.owner ?? '—'}</Table.Td>
                        <Table.Td>{formatUpdatedAt(table.updated_at)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Empty message="No tables matched this search." />
            ))}
        </>
      )}

      {!catalogName && !catalogsQuery.isLoading && !catalogsQuery.error && (
        <Empty message="Select a catalog to browse its schemas and tables." />
      )}

      <Modal
        opened={Boolean(selectedTable)}
        onClose={() => selectTable(null)}
        title={selectedTable?.full_name ?? 'Table details'}
        size="lg"
        centered
      >
        {selectedTable && (
          <Stack>
            <Group gap="xs">
              <Badge variant="light">{selectedTable.table_type ?? 'unknown'}</Badge>
              {selectedTable.data_source_format && (
                <Badge variant="light" color="gray">
                  {selectedTable.data_source_format}
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {selectedTable.comment ?? 'No description is available for this table.'}
            </Text>
            <Text size="xs" c="dimmed">
              Owner: {selectedTable.owner ?? 'unknown'} · Updated:{' '}
              {formatUpdatedAt(selectedTable.updated_at)}
            </Text>
            <TableColumns table={selectedTable} />
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
